import React, { useState, useEffect } from 'react';
import Sidebar from '../../../components/shared/Sidebar';
import PageLoading from '../../../components/shared/PageLoading';
import Button from '../../../components/shared/Button';
import StatusBadge from '../../../components/shared/StatusBadge';

/**
 * USOKIE Platform Parties Registry
 * Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
 */
const PartyList = () => {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [partyTypeFilter, setPartyTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchParties();
  }, [searchQuery, partyTypeFilter, statusFilter, currentPage]);

  const fetchParties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 25,
        ...(searchQuery && { q: searchQuery }),
        ...(partyTypeFilter && { party_type: partyTypeFilter }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await fetch(`/api/v1/parties?${params}`);
      const data = await response.json();

      setParties(data.parties || []);
      setTotalPages(data.meta?.total_pages || 1);
      setTotalCount(data.meta?.total_count || 0);
    } catch (error) {
      console.error('Error fetching parties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePartyTypeChange = (e) => {
    setPartyTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (loading && parties.length === 0) {
    return <PageLoading message="Loading Parties" />;
  }

  return (
    <div className="flex h-screen bg-black">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Document Header */}
          <div className="mb-8 pb-6 border-b border-[rgba(255,251,247,0.2)]">
            <div className="flex items-end justify-between">
              <div>
                <span
                  className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  REGISTRY PTY.LIST
                </span>
                <h1 className="text-2xl font-bold text-[#fffbf7] tracking-[0.08em] uppercase">
                  Parties Registry
                </h1>
              </div>
              <Button href="/parties/new">
                + Add New Party
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <span
                className="text-xs text-[#a1c6a2] tracking-wider"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                FILTERS
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors placeholder:text-[rgba(255,251,247,0.3)]"
              />
              <select
                value={partyTypeFilter}
                onChange={handlePartyTypeChange}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
              >
                <option value="">All Party Types</option>
                <option value="author">Author</option>
                <option value="publisher">Publisher</option>
                <option value="rights_holder">Rights Holder</option>
                <option value="licensee">Licensee</option>
                <option value="agent">Agent</option>
                <option value="other">Other</option>
              </select>
              <select
                value={statusFilter}
                onChange={handleStatusChange}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          {/* Results Info */}
          <div className="mb-6">
            <span
              className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em]"
              style={{ fontFamily: "'GT Pressura Mono', monospace" }}
            >
              SHOWING {parties.length} OF {totalCount} PARTIES
            </span>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border border-[rgba(255,251,247,0.2)] border-t-[#a1c6a2] animate-spin" />
            </div>
          )}

          {/* Grid */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {parties.length === 0 ? (
                <div className="col-span-full border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] p-12 text-center">
                  <p className="text-sm text-[rgba(255,251,247,0.5)]">
                    No parties found. Add your first one!
                  </p>
                </div>
              ) : (
                parties.map(party => (
                  <div
                    key={party.id}
                    className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] hover:border-[rgba(255,251,247,0.2)] transition-colors"
                  >
                    {/* Card Header */}
                    <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                      <div className="flex items-center justify-between">
                        <a
                          href={`/parties/${party.id}`}
                          className="text-sm font-semibold text-[#fffbf7] hover:text-[#a1c6a2] transition-colors tracking-wide"
                        >
                          {party.name}
                        </a>
                        <StatusBadge status={party.status} />
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-6 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span
                          className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                        >
                          Type
                        </span>
                        <span className="text-[rgba(255,251,247,0.6)] capitalize">
                          {party.party_type?.replace('_', ' ') || '-'}
                        </span>
                      </div>

                      {party.contact_email && (
                        <div className="flex justify-between text-sm">
                          <span
                            className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            Email
                          </span>
                          <span className="text-[rgba(255,251,247,0.6)]">
                            {party.contact_email}
                          </span>
                        </div>
                      )}

                      {party.contact_phone && (
                        <div className="flex justify-between text-sm">
                          <span
                            className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            Phone
                          </span>
                          <span
                            className="text-[rgba(255,251,247,0.6)]"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            {party.contact_phone}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between text-sm">
                        <span
                          className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                        >
                          IP Objects
                        </span>
                        <span
                          className="px-2 py-0.5 text-[10px] border border-[#a1c6a2] text-[#a1c6a2] tracking-wider"
                          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                        >
                          {party.owned_ip_objects?.length || 0}
                        </span>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className="flex gap-2 px-6 py-4 border-t border-[rgba(255,251,247,0.1)] bg-black">
                      <Button href={`/parties/${party.id}`} variant="secondary" size="sm">
                        View
                      </Button>
                      <Button href={`/parties/${party.id}/edit`} variant="secondary" size="sm">
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-6 mt-8 pt-6 border-t border-[rgba(255,251,247,0.1)]">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span
                className="text-[10px] text-[rgba(255,251,247,0.5)] tracking-wider"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PartyList;
